import { AgGrid } from "@jsq/ds/core";
import { URL_FILTER_FIELDS } from "./constants";

// ── Legacy base filter (kept for backward compatibility) ──────────────────────

type BaseFilterData = {
  arenaId?: string | null;
  arenaName?: string | null;
  activeStatuses?: string[] | null;
};

/**
 * @deprecated Use buildClientUrlFilters, which handles both legacy and
 * WorkflowEngine key/value formats based on the crltWorkflowEngine flag.
 */
export const buildBaseFilters = (data: BaseFilterData): AgGrid.FilterModel => {
  const filterModel: AgGrid.FilterModel = {};

  if (data.arenaId && data.arenaName) {
    filterModel[URL_FILTER_FIELDS.ARENA_NAME] = {
      filterType: "set",
      values: [data.arenaName],
    };
  }

  if (data.activeStatuses?.length) {
    filterModel[URL_FILTER_FIELDS.STATUS] = {
      filterType: "set",
      values: data.activeStatuses,
    };
  }

  return filterModel;
};

// ── Client filter utility ─────────────────────────────────────────────────────

export type ClientDueDateRangeKey = "dueIn5Days" | "overdue";

export type ClientFilterData = {
  arenaId?: string | null;
  arenaName?: string | null;
  activeStatuses?: string[] | null;
  dateRangeMetadata?: {
    dueIn5DaysStart: string | null;
    dueIn5DaysEnd: string | null;
    overdueStart: string | null;
    overdueEnd: string | null;
  } | null;
};

/**
 * Builds the URL filter model for navigating from Clients view to the Workflows tab.
 *
 * The filter key and value differ between engines:
 *
 * | Filter   | Legacy (crltWorkflowEngine=OFF)          | WorkflowEngine (crltWorkflowEngine=ON)          |
 * |----------|------------------------------------------|-------------------------------------------------|
 * | Client   | key: "customers",  value: arenaName      | key: "clientName", value: arenaId (client ID)   |
 * | Status   | key: "status",     value: status enum    | key: "status",     value: status enum (same)    |
 * | Due date | key: "dueDate",    AG Grid date filter   | key: "dueDate",    AG Grid date filter (same)   |
 */
export const buildClientUrlFilters = (
  data: ClientFilterData,
  useWorkflowEngine: boolean,
  dueDateRangeKey?: ClientDueDateRangeKey,
): AgGrid.FilterModel => {
  const filterModel: AgGrid.FilterModel = {};

  if (data.arenaId && data.arenaName) {
    if (useWorkflowEngine) {
      // WorkflowEngine: clientName column stores the clientId as its set-filter value
      filterModel[URL_FILTER_FIELDS.CLIENT_NAME] = {
        filterType: "set",
        values: [data.arenaId],
      };
    } else {
      // Legacy Jira grid: customers column stores the arena display name
      filterModel[URL_FILTER_FIELDS.ARENA_NAME] = {
        filterType: "set",
        values: [data.arenaName],
      };
    }
  }

  if (data.activeStatuses?.length) {
    filterModel[URL_FILTER_FIELDS.STATUS] = {
      filterType: "set",
      values: data.activeStatuses,
    };
  }

  if (dueDateRangeKey && data.dateRangeMetadata) {
    const { dateRangeMetadata } = data;
    const dateFrom =
      dueDateRangeKey === "dueIn5Days"
        ? dateRangeMetadata.dueIn5DaysStart
        : dateRangeMetadata.overdueStart;
    const dateTo =
      dueDateRangeKey === "dueIn5Days"
        ? dateRangeMetadata.dueIn5DaysEnd
        : dateRangeMetadata.overdueEnd;

    if (dateFrom && dateTo) {
      filterModel[URL_FILTER_FIELDS.DUE_DATE] = {
        filterType: "date",
        type: "inRange",
        dateFrom,
        dateTo,
      };
    } else if (dateFrom) {
      filterModel[URL_FILTER_FIELDS.DUE_DATE] = {
        filterType: "date",
        type: useWorkflowEngine ? "greaterThan" : "greaterThanOrEqual",
        dateFrom,
      };
    } else if (dateTo) {
      filterModel[URL_FILTER_FIELDS.DUE_DATE] = {
        filterType: "date",
        type: "lessThan",
        dateFrom: dateTo,
      };
    }
  }

  return filterModel;
};

// ── Individual filter utility ─────────────────────────────────────────────────

export type IndividualDueDateRangeKey =
  "due02Days" | "due35Days" | "due614Days" | "due15PlusDays";

// Typed to exactly match the fields returned by GetIndividualView.dateRangeMetadata
type IndividualDateRangeMetadata = Partial<
  Record<`${IndividualDueDateRangeKey}${"Start" | "End"}`, string | null>
>;

export type IndividualFilterData = {
  assigneeId: string;
  assigneeName: string;
  activeStatuses?: string[] | null;
  dateRangeMetadata?: IndividualDateRangeMetadata | null;
};

/**
 * Builds the URL filter model for navigating from Individuals view to the Workflows tab.
 *
 * The filter key and value differ between engines:
 *
 * | Filter   | Legacy (crltWorkflowEngine=OFF)          | WorkflowEngine (crltWorkflowEngine=ON)            |
 * |----------|------------------------------------------|---------------------------------------------------|
 * | Assignee | key: "assignedTo", value: assigneeName   | key: "assignee",   value: assigneeId (user ID)    |
 * | Status   | key: "status",     value: status enum    | key: "status",     value: status enum (same)      |
 * | Due date | key: "dueDate",    AG Grid date filter   | key: "dueDate",    AG Grid date filter (same)     |
 */
export const buildIndividualUrlFilters = (
  data: IndividualFilterData,
  useWorkflowEngine: boolean,
  dueDateRangeKey?: IndividualDueDateRangeKey,
): AgGrid.FilterModel => {
  const filterModel: AgGrid.FilterModel = {};

  if (useWorkflowEngine) {
    // WorkflowEngine: assignee column stores the userId as its set-filter value
    if (data.assigneeId) {
      filterModel[URL_FILTER_FIELDS.ASSIGNEE] = {
        filterType: "set",
        values: [data.assigneeId],
      };
    }
  } else {
    // Legacy Jira grid: assignedTo column stores the assignee display name
    if (data.assigneeName) {
      filterModel[URL_FILTER_FIELDS.ASSIGNED_TO] = {
        filterType: "set",
        values: [data.assigneeName],
      };
    }
  }

  if (data.activeStatuses?.length) {
    filterModel[URL_FILTER_FIELDS.STATUS] = {
      filterType: "set",
      values: data.activeStatuses,
    };
  }

  if (dueDateRangeKey && data.dateRangeMetadata) {
    const dateFrom = data.dateRangeMetadata[`${dueDateRangeKey}Start`];
    const dateTo = data.dateRangeMetadata[`${dueDateRangeKey}End`];

    if (dateFrom && dateTo) {
      filterModel[URL_FILTER_FIELDS.DUE_DATE] = {
        filterType: "date",
        type: "inRange",
        dateFrom,
        dateTo,
      };
    } else if (dateFrom) {
      filterModel[URL_FILTER_FIELDS.DUE_DATE] = {
        filterType: "date",
        type: useWorkflowEngine ? "greaterThan" : "greaterThanOrEqual",
        dateFrom,
      };
    }
  }

  return filterModel;
};
